/**
 * pickup-instructions slug — create-form input schema (shared transport layer).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 * @see docs/SHIPPING_INSTRUCTIONS_SEMANTICS.md — deliveryNotes prefill
 */
import { z } from "zod";
import { CarrierInstructionCostReferenceSchema } from "~/shared/documents/carrier-instruction-costs";
import { OrderDocumentSourceTypeSchema } from "~/shared/documents/order-document-source";

import { DisplayUomOverrideSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { TypedSignatureInputSchema } from "~/shared/documents/schemas/typed-signature.schema";
import { DocumentShippableEntitySchema } from "~/utils/address-formatters";

const optionalTrimmedString = z.string().trim().optional().default("");

export const PickupInstructionsLineItemInputSchema = z.object({
  orderItemId: z.string().uuid(),
  description: z.string(),
  producer: optionalTrimmedString,
  quantity: optionalTrimmedString,
  pickUpDate: optionalTrimmedString,
  pickUpAppointment: optionalTrimmedString,
  releaseNumber: optionalTrimmedString,
  deliveryDate: optionalTrimmedString,
  deliveryAppointment: optionalTrimmedString,
});

export const PickupInstructionsDocumentInputSchema = z.object({
  /** Legacy transport name: identifies the source selected by sourceType. */
  orderId: z.string().uuid(),
  sourceType: OrderDocumentSourceTypeSchema.optional(),
  addressOverrides: z
    .object({
      pickUpLocation: DocumentShippableEntitySchema.optional(),
      consignor: DocumentShippableEntitySchema.optional(),
      consignee: DocumentShippableEntitySchema.optional(),
    })
    .optional(),
  documentDate: optionalTrimmedString,
  carrier: optionalTrimmedString,
  carrierContact: optionalTrimmedString,
  reLine: optionalTrimmedString,
  senderName: optionalTrimmedString,
  pickUpLocation: optionalTrimmedString,
  supplier: optionalTrimmedString,
  pickUpNotes: optionalTrimmedString,
  includeBlindPickupDelivery: z.boolean().default(false),
  borderCrossingNotes: optionalTrimmedString,
  consignor: optionalTrimmedString,
  consignee: optionalTrimmedString,
  deliveryNotes: optionalTrimmedString,
  /** Missing keeps legacy scalar requests compatible; [] explicitly omits rates. */
  selectedRateCosts: z.array(CarrierInstructionCostReferenceSchema).optional(),
  rateDescription: optionalTrimmedString,
  rateAmount: optionalTrimmedString,
  rateCurrency: optionalTrimmedString,
  lineItems: z.array(PickupInstructionsLineItemInputSchema).min(1),
  displayUomOverride: DisplayUomOverrideSchema,
  typedSignature: TypedSignatureInputSchema,
});

export type PickupInstructionsDocumentInput = z.infer<typeof PickupInstructionsDocumentInputSchema>;
